/* ==========================================================================
   OSERE — Cuándo tomarlo

   Dos trabajos, y los dos son mejoras que se AÑADEN: si este archivo no llega
   a ejecutarse —bloqueado, error de red, navegador viejo— la sección se ve
   entera y con sus fotos. Nunca se resta de algo que ya funciona.

   1. Aparición al entrar en pantalla. El estado oculto lo gatea el atributo
      `data-os-anim`, que pone este script. Sin script, el CSS no esconde nada.

   2. Los vídeos de las tarjetas. Aquí está casi toda la lógica, porque un
      vídeo en bucle es la pieza más cara de la página y la que más fácil se
      convierte en un problema ajeno.

   ⚠️ POR QUÉ EL VÍDEO NO ARRANCA SOLO NI SIEMPRE.
   Cuatro cortafuegos, los mismos que ya usa el bucle del hero:

   · `prefers-reduced-motion` — quien pidió que nada se mueva, nada se mueve.
   · `saveData` y 2G/3G — un bucle decorativo en red lenta se paga en datos
     ajenos, y esta sección puede tener CUATRO a la vez.
   · Móvil — apagado salvo que se active en los ajustes. El 70–80% del tráfico
     es móvil; cuatro vídeos en 4G no son una decisión de diseño, son una
     factura.
   · Fuera de pantalla — solo se reproduce lo que se está viendo, y se pausa al
     salir. Sin esto, cuatro vídeos siguen decodificando mientras lees el pie.

   El `<video>` nace con `preload="none"` y sin `autoplay`, y eso basta: medido
   en la página real, 0 peticiones y 0 bytes mientras la sección sigue fuera de
   pantalla. El primer byte lo pide el play() de aquí abajo.

   El vídeo solo se muestra con el evento `playing`: si el autoplay se bloquea
   —Safari con ahorro de batería, por ejemplo— se queda la foto y no hay
   parpadeo.
   ========================================================================== */

(function () {
  'use strict';

  var QUIETO = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Red lenta o ahorro de datos. `connection` no existe en Safari, y ahí la
     ausencia de dato no puede significar «adelante con todo»: se asume una red
     buena solo cuando el navegador NO dice lo contrario, que es lo que hace
     esta lectura. */
  function redPobre() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g' || t === '3g';
  }

  function montar(seccion) {
    /* ---- 1. Aparición --------------------------------------------------- */

    seccion.setAttribute('data-os-anim', 'true');

    var piezas = seccion.querySelectorAll('[data-os-reveal]');
    var i;

    if (QUIETO || !('IntersectionObserver' in window)) {
      for (i = 0; i < piezas.length; i++) piezas[i].classList.add('os-in');
    } else {
      var verAparecer = new IntersectionObserver(
        function (entradas) {
          entradas.forEach(function (e) {
            if (!e.isIntersecting) return;
            e.target.classList.add('os-in');
            /* Se deja de observar al aparecer: si no, la tarjeta se re-anima
               cada vez que vuelves a subir. */
            verAparecer.unobserve(e.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );
      for (i = 0; i < piezas.length; i++) verAparecer.observe(piezas[i]);
    }

    /* ---- 2. Vídeos de las tarjetas -------------------------------------- */

    var envoltorios = seccion.querySelectorAll('[data-os-video]');
    if (!envoltorios.length) return;

    var permiteMovil = seccion.getAttribute('data-os-video-movil') === 'true';
    var esMovil = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;

    /* Si no se puede reproducir, se deja la foto y se acabó. El `<video>` sigue
       en el marcado —quitarlo obligaría a recargar la sección para volver a
       ponerlo cuando el ajuste cambie— pero nadie llama nunca a su play(), así
       que no cuesta un byte. Salir por aquí es la vía normal en móvil. */
    if (QUIETO || redPobre() || (esMovil && !permiteMovil)) return;

    if (!('IntersectionObserver' in window)) return;

    var verVideos = new IntersectionObserver(
      function (entradas) {
        entradas.forEach(function (e) {
          var v = e.target.querySelector('video');
          if (!v) return;

          if (e.isIntersecting) {
            /* El `<video>` nace con `preload="none"`, así que hasta este
               play() no ha costado un solo byte. Verificado en la página real:
               0 peticiones con la sección aún fuera de pantalla. */
            var p = v.play();
            /* play() devuelve una promesa que RECHAZA si el navegador bloquea
               el autoplay. Sin este catch salta un error no capturado en
               consola en cada tarjeta y parece que la sección está rota. */
            if (p && p.catch) p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.15 }
    );

    for (i = 0; i < envoltorios.length; i++) {
      var env = envoltorios[i];
      var vid = env.querySelector('video');
      if (!vid) continue;

      /* El vídeo solo se ve cuando está reproduciendo de verdad. Es lo que
         evita el fotograma en negro entre que se pide el archivo y arranca. */
      (function (env, vid) {
        vid.addEventListener('playing', function () {
          env.setAttribute('data-os-reproduciendo', '');
        });
        /* Si el archivo falla, se vuelve a la foto en vez de dejar un hueco. */
        vid.addEventListener('error', function () {
          env.removeAttribute('data-os-reproduciendo');
        });
      })(env, vid);

      verVideos.observe(env);
    }
  }

  function init() {
    var secciones = document.querySelectorAll('[data-os-escenarios]');
    for (var i = 0; i < secciones.length; i++) montar(secciones[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* El personalizador reconstruye la sección al editarla y se lleva por delante
     los observadores. Sin esto, la clienta edita un ajuste y la sección se le
     queda invisible —con `data-os-anim` puesto y nadie que añada `os-in`—, que
     es la peor forma de parecer rota. */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-os-escenarios]');
    if (s) montar(s);
  });
})();
